import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useConfig } from '../context/ConfigContext';

const CACHE_BUSTER = Date.now();

const Agenda = () => {
    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeGroup, setActiveGroup] = useState(null);
    const [activeSubgroup, setActiveSubgroup] = useState(null);
    const { config } = useConfig();

    useEffect(() => {
        fetchAgenda();
    }, []);

    useEffect(() => {
        if (events.length > 0) {
            const availableGroups = Array.from(new Set(events.map(e => e.group_name).filter(Boolean)));
            if (availableGroups.length > 0 && !activeGroup) {
                setActiveGroup(availableGroups[0]);
            }
        }
    }, [events, activeGroup]);

    useEffect(() => {
        if (events.length > 0 && activeGroup) {
            const activeGroupEvents = events.filter(e => e.group_name === activeGroup);
            const availableSubgroups = Array.from(new Set(activeGroupEvents.map(e => e.subgroup_name).filter(Boolean)));
            if (availableSubgroups.length > 0) {
                setActiveSubgroup(prev => {
                    if (!prev || !availableSubgroups.includes(prev)) {
                        return availableSubgroups[0];
                    }
                    return prev;
                });
            } else {
                setActiveSubgroup(null);
            }
        }
    }, [events, activeGroup]);

    const fetchAgenda = async () => {
        setIsLoading(true);
        try {
            const res = await api.get('/agenda?status=active');
            setEvents(res.data);
        } catch (error) {
            console.error("Error fetching agenda", error);
        } finally {
            setIsLoading(false);
        }
    };

    const pageDesign = config?.pageDesigns?.agenda || {};
    const isImageOnly = pageDesign.agendaImageOnly;

    // Helper functions for flags
    const getFlagEmoji = (country) => {
        if (!country) return '';
        const name = country.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const flags = {
            'colombia': '🇨🇴', 'co': '🇨🇴',
            'mexico': '🇲🇽', 'mx': '🇲🇽',
            'argentina': '🇦🇷', 'ar': '🇦🇷',
            'chile': '🇨🇱', 'cl': '🇨🇱',
            'peru': '🇵🇪', 'pe': '🇵🇪',
            'ecuador': '🇪🇨', 'ec': '🇪🇨',
            'espana': '🇪🇸', 'es': '🇪🇸',
            'estados unidos': '🇺🇸', 'usa': '🇺🇸', 'us': '🇺🇸',
            'brasil': '🇧🇷', 'brazil': '🇧🇷', 'br': '🇧🇷',
            'venezuela': '🇻🇪', 've': '🇻🇪',
            'uruguay': '🇺🇾', 'uy': '🇺🇾',
            'paraguay': '🇵🇾', 'py': '🇵🇾',
            'bolivia': '🇧🇴', 'bo': '🇧🇴',
            'panama': '🇵🇦', 'pa': '🇵🇦',
            'costa rica': '🇨🇷', 'cr': '🇨🇷',
            'cuba': '🇨🇺', 'cu': '🇨🇺',
            'republica dominicana': '🇩🇴', 'do': '🇩🇴',
            'puerto rico': '🇵🇷', 'pr': '🇵🇷',
            'honduras': '🇭🇳', 'hn': '🇭🇳',
            'el salvador': '🇸🇻', 'sv': '🇸🇻',
            'guatemala': '🇬🇹', 'gt': '🇬🇹',
            'nicaragua': '🇳🇮', 'ni': '🇳🇮',
            'italia': '🇮🇹', 'it': '🇮🇹',
            'francia': '🇫🇷', 'fr': '🇫🇷',
            'alemania': '🇩🇪', 'de': '🇩🇪',
            'reino unido': '🇬🇧', 'uk': '🇬🇧', 'gb': '🇬🇧',
            'canada': '🇨🇦', 'ca': '🇨🇦'
        };
        return flags[name] || '';
    };

    const groups = Array.from(new Set(events.map(e => e.group_name).filter(Boolean)));
    const activeGroupEvents = activeGroup ? events.filter(e => e.group_name === activeGroup) : events;
    let subgroups = Array.from(new Set(activeGroupEvents.map(e => e.subgroup_name).filter(Boolean)));
    if (pageDesign?.subgroupOrder) {
        const orderArr = pageDesign.subgroupOrder.split(',').map(s => s.trim().toUpperCase());
        subgroups = subgroups.sort((a, b) => {
            const indexA = orderArr.indexOf(a.toUpperCase());
            const indexB = orderArr.indexOf(b.toUpperCase());
            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
            return a.localeCompare(b);
        });
    }

    const filteredEvents = events.filter(e => {
        const matchesGroup = !activeGroup || e.group_name === activeGroup;
        const matchesSubgroup = !activeSubgroup || e.subgroup_name === activeSubgroup;
        return matchesGroup && matchesSubgroup;
    });

    const getActiveLegalConfig = (group, subgroup) => {
        const glts = pageDesign.groupLegalTexts || [];
        const normalize = (str) => (str || '').trim().toLowerCase();

        const nGroup = normalize(group);
        const nSub = normalize(subgroup);

        let match = glts.find(g => normalize(g.groupName) === nGroup && normalize(g.subgroupName) === nSub);
        if (match && match.legalTextUrl) return match;

        match = glts.find(g => normalize(g.groupName) === nGroup && !normalize(g.subgroupName));
        if (match && match.legalTextUrl) return match;

        if (nSub) {
            match = glts.find(g => !normalize(g.groupName) && normalize(g.subgroupName) === nSub);
            if (match && match.legalTextUrl) return match;
        }

        return {
            showLegalText: pageDesign.showLegalText,
            legalTextUrl: pageDesign.legalTextUrl
        };
    };

    // Group events by date and block
    const groupedData = filteredEvents.reduce((acc, event) => {
        const dateStr = new Date(event.date).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
        const capitalizedDate = dateStr.charAt(0).toUpperCase() + dateStr.slice(1);

        if (!acc[capitalizedDate]) acc[capitalizedDate] = {};

        const block = event.block_name || 'Sin Bloque';
        if (!acc[capitalizedDate][block]) acc[capitalizedDate][block] = {
            name: block,
            color: event.block_color || '#0a58ca',
            events: []
        };

        const existingEvent = acc[capitalizedDate][block].events.find(e => e.title === event.title && e.start_time === event.start_time);

        if (existingEvent) {
            if (event.speaker_name) {
                existingEvent.speakers = existingEvent.speakers || [];
                if (!existingEvent.speakers.some(s => s.name === event.speaker_name)) {
                    existingEvent.speakers.push({ name: event.speaker_name, country: event.speaker_country || event.country });
                }
            } else if (event.country) {
                existingEvent.speakers = existingEvent.speakers || [];
                if (!existingEvent.speakers.some(s => s.country === event.country)) {
                    existingEvent.speakers.push({ name: '', country: event.country });
                }
            }
        } else {
            const newEvent = { ...event, speakers: [] };
            if (event.speaker_name) {
                newEvent.speakers.push({ name: event.speaker_name, country: event.speaker_country || event.country });
            } else if (event.country) {
                newEvent.speakers.push({ name: '', country: event.country });
            }
            acc[capitalizedDate][block].events.push(newEvent);
        }

        return acc;
    }, {});

    const getIcon = (type) => {
        if (type === 'breakfast') return '🥐';
        if (type === 'coffee') return '☕';
        if (type === 'lunch') return '🍽️';
        if (type === 'closing') return '✈️';
        if (type === 'other') return '✨';
        return null;
    };

    // Determine the active visual theme colors based on group/subgroup for absolute fidelity
    const isTepezzaActive = activeSubgroup === "TEPEZZA" || activeGroup === "TEPEZZA";
    const primaryThemeColor = isTepezzaActive ? "#5bb343" : "#0a58ca"; // Green for TEPEZZA, Corporate Blue otherwise
    const secondaryThemeColor = "#ffa800"; // Orange/Yellow

    // Dynamically retrieve font colors from admin config for groups and subgroups
    const activeSubgroupConfig = config?.pageDesigns?.agenda?.groupColors?.find(g => 
        (g.groupName === activeGroup && g.subgroupName === activeSubgroup) || 
        g.groupName === activeSubgroup
    );
    const activeGroupConfig = config?.pageDesigns?.agenda?.groupColors?.find(g => g.groupName === activeGroup && !g.subgroupName);

    // Try to find a config for the subgroup if it exists, otherwise fallback to group
    const dynamicGroupFontColor = activeSubgroupConfig?.globalFontColor || activeSubgroupConfig?.primaryColor || activeGroupConfig?.globalFontColor || activeGroupConfig?.primaryColor || primaryThemeColor;

    // We will use dynamicGroupFontColor for the texts in the agenda
    const finalFontColor = pageDesign.globalFontColor || dynamicGroupFontColor;

    // Inject the final font color as a CSS variable so Header.js can read it for the main title
    React.useEffect(() => {
        document.documentElement.style.setProperty('--agenda-font-color', finalFontColor);
        return () => {
            document.documentElement.style.removeProperty('--agenda-font-color');
        };
    }, [finalFontColor]);

    const imageAgendas = pageDesign.imageAgendas || [];

    if (isImageOnly) {
        if (imageAgendas.length > 0) {
            const currentActiveGroup = activeGroup || imageAgendas[0]?.group_name;
            const activeGroupItems = imageAgendas.filter(a => a.group_name === currentActiveGroup);
            
            const sortSubgroups = (subgroupsArray) => {
                if (!pageDesign.subgroupOrder) return subgroupsArray;
                const orderArr = pageDesign.subgroupOrder.split(',').map(s => s.trim().toUpperCase());
                return subgroupsArray.sort((a, b) => {
                    const indexA = orderArr.indexOf(a.toUpperCase());
                    const indexB = orderArr.indexOf(b.toUpperCase());
                    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                    if (indexA !== -1) return -1;
                    if (indexB !== -1) return 1;
                    return a.localeCompare(b);
                });
            };

            const imageSubgroups = sortSubgroups(Array.from(new Set(activeGroupItems.map(a => a.subgroup_name).filter(Boolean))));

            // Auto-select first subgroup if none is active but subgroups exist for this group
            let currentActiveSubgroup = activeSubgroup;
            if (imageSubgroups.length > 0 && !imageSubgroups.includes(currentActiveSubgroup)) {
                currentActiveSubgroup = imageSubgroups[0];
            } else if (imageSubgroups.length === 0) {
                currentActiveSubgroup = null;
            }

            const activeImageItem = activeGroupItems.find(a =>
                (currentActiveSubgroup ? a.subgroup_name === currentActiveSubgroup : true)
            ) || activeGroupItems[0];
            const activeImage = activeImageItem?.staticImage || imageAgendas[0]?.staticImage;

            const uniqueGroups = Array.from(new Set(imageAgendas.map(a => a.group_name).filter(Boolean)));

            return (
                <div className="animate-fadeIn max-w-5xl mx-auto font-sans pt-4 sm:pt-6">
                    <div className="flex flex-col items-center gap-4 mb-1 pb-1">
                        <div className="flex justify-center gap-4 w-full overflow-x-auto pb-2 px-4 custom-scrollbar">
                            {uniqueGroups.map(group => {
                                const isSelected = currentActiveGroup === group;
                                return (
                                    <button
                                        key={group}
                                        onClick={() => {
                                            setActiveGroup(group);
                                            // Subgroup auto-select handles itself in the render logic above when activeGroup changes
                                        }}
                                        className={`px-8 py-3 rounded-lg font-black uppercase tracking-widest text-[12px] sm:text-[14px] transition-all whitespace-nowrap shadow-md ${isSelected
                                            ? `bg-[#ffa800] text-white`
                                            : 'bg-[#0a58ca] text-white hover:opacity-90'
                                            }`}
                                    >
                                        {group}
                                    </button>
                                );
                            })}
                        </div>

                        {pageDesign.subgroupPosition !== 'bottom' && imageSubgroups.length > 0 && (
                            <div className="flex justify-center gap-3 w-full overflow-x-auto pb-2 px-4 custom-scrollbar mt-2">
                                {imageSubgroups.map(subg => {
                                    const isSelected = currentActiveSubgroup === subg;
                                    return (
                                        <button
                                            key={subg}
                                            onClick={() => setActiveSubgroup(subg)}
                                            style={{ backgroundColor: isSelected ? "#ffa800" : "#0a58ca" }}
                                            className="px-6 py-2.5 rounded-lg font-black uppercase tracking-widest text-[11px] sm:text-[12px] text-white transition-all shadow-md hover:scale-[1.02] active:scale-95 duration-200 min-w-[110px] text-center"
                                        >
                                            {subg}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {(() => {
                            const downloadUrl = activeImageItem?.downloadUrl || activeGroupItems.find(a => a.downloadUrl)?.downloadUrl;
                            if (downloadUrl) {
                                return (
                                    <a
                                        href={downloadUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="bg-slate-100 text-slate-800 px-6 py-3 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-all border border-slate-300 shadow-md flex items-center gap-2 mt-2"
                                    >
                                        <span>⬇️</span> Descargar Agenda
                                    </a>
                                );
                            }
                            return null;
                        })()}
                    </div>
                    <div className="flex flex-col items-center p-0 bg-transparent">
                        {activeImage ? (
                            <img
                                src={`${activeImage}${activeImage.includes('?') ? '&' : '?'}v=${CACHE_BUSTER}`}
                                alt={`Agenda ${currentActiveGroup} ${currentActiveSubgroup || ''}`}
                                className="w-[90vw] md:w-auto max-w-full h-[80vh] object-contain shadow-2xl mb-10 mx-auto"
                            />
                        ) : (
                            <div className="text-slate-400 py-10 uppercase tracking-widest font-black text-xs">Sin imagen configurada</div>
                        )}
                    </div>
                    {pageDesign.subgroupPosition === 'bottom' && imageSubgroups.length > 0 && (
                        <div className="flex justify-center gap-3 w-full overflow-x-auto pb-2 px-4 custom-scrollbar mt-6 mb-4">
                            {imageSubgroups.map(subg => {
                                const isSelected = currentActiveSubgroup === subg;
                                return (
                                    <button
                                        key={subg}
                                        onClick={() => setActiveSubgroup(subg)}
                                        style={{ backgroundColor: isSelected ? "#ffa800" : "#0a58ca" }}
                                        className="px-6 py-2.5 rounded-lg font-black uppercase tracking-widest text-[11px] sm:text-[12px] text-white transition-all shadow-md hover:scale-[1.02] active:scale-95 duration-200 min-w-[110px] text-center"
                                    >
                                        {subg}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {(() => {
                        const legalConfig = getActiveLegalConfig(currentActiveGroup, currentActiveSubgroup);
                        return legalConfig?.showLegalText && legalConfig?.legalTextUrl && (
                            <footer className="w-full p-4 sm:p-6 mt-auto flex justify-center items-center z-40 select-none bg-transparent">
                                {/\.(pdf)$/i.test(legalConfig.legalTextUrl) ? (
                                    <a href={`${legalConfig.legalTextUrl}${legalConfig.legalTextUrl.includes('?') ? '&' : '?'}v=${CACHE_BUSTER}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline font-bold text-sm">Ver Información Legal</a>
                                ) : (
                                    <img
                                        src={`${legalConfig.legalTextUrl}${legalConfig.legalTextUrl.includes('?') ? '&' : '?'}v=${CACHE_BUSTER}`}
                                        alt="Información Legal"
                                        className="max-w-[90vw] md:max-w-2xl h-auto object-contain pointer-events-auto"
                                    />
                                )}
                            </footer>
                        );
                    })()}
                </div>
            );
        }
    }

    return (
        <div className="animate-fadeIn max-w-4xl mx-auto pb-32 font-sans pt-4 sm:pt-6">
            {/* The Floating elements configured in apply_amgen_designs.js are automatically renderered by Layout.js above */}

            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="w-12 h-12 border-4 border-slate-200 border-t-[#0a58ca] rounded-full animate-spin"></div>
                    <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.4em] mt-6 animate-pulse">Cargando Agenda...</p>
                </div>
            ) : (
                <div className="space-y-12 px-4 sm:px-6">
                    {/* Toggles / Tabs de Días (DÍA 1, DÍA 2) as in Mockups 2 and 3 */}
                    {groups.length > 0 && (
                        <div className="flex flex-col items-center gap-4 mb-6">
                            <div className="flex justify-center gap-6 w-full overflow-x-auto pb-2 px-4 custom-scrollbar">
                                {groups.map(group => {
                                    const isSelected = activeGroup === group;
                                    return (
                                        <button
                                            key={group}
                                            onClick={() => setActiveGroup(group)}
                                            style={{ backgroundColor: isSelected ? "#ffa800" : "#0a58ca" }}
                                            className="px-8 py-3 rounded-lg font-black uppercase tracking-widest text-[13px] sm:text-[15px] text-white transition-all shadow-lg hover:scale-[1.02] active:scale-95 duration-200 min-w-[140px] text-center"
                                        >
                                            {group}
                                        </button>
                                    );
                                })}
                            </div>

                            {(() => {
                                const activeSubgroupConfig = config?.pageDesigns?.agenda?.groupColors?.find(g => g.groupName === activeGroup && g.subgroupName === activeSubgroup);
                                const activeGroupConfig = config?.pageDesigns?.agenda?.groupColors?.find(g => g.groupName === activeGroup && !g.subgroupName);
                                
                                let downloadUrl = activeSubgroupConfig?.downloadUrl || activeGroupConfig?.downloadUrl;
                                
                                if (!downloadUrl && config?.pageDesigns?.agenda?.imageAgendas) {
                                    const fallbackSub = config.pageDesigns.agenda.imageAgendas.find(a => a.group_name === activeGroup && a.subgroup_name === activeSubgroup && a.downloadUrl);
                                    const fallbackGroup = config.pageDesigns.agenda.imageAgendas.find(a => a.group_name === activeGroup && !a.subgroup_name && a.downloadUrl);
                                    if (fallbackSub) downloadUrl = fallbackSub.downloadUrl;
                                    else if (fallbackGroup) downloadUrl = fallbackGroup.downloadUrl;
                                }

                                if (downloadUrl) {
                                    return (
                                        <a
                                            href={downloadUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="bg-slate-100 text-slate-800 px-6 py-3 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-all border border-slate-300 shadow-md flex items-center gap-2"
                                        >
                                            <span>⬇️</span> Descargar Agenda
                                        </a>
                                    );
                                }
                                return null;
                            })()}

                            {/* Subgrupos / Laboratorios (TEPEZZA, UPLIZNA, BKEMV) */}
                            {pageDesign.subgroupPosition !== 'bottom' && subgroups.length > 0 && (
                                <div className="flex justify-center gap-3 w-full overflow-x-auto pb-2 px-4 custom-scrollbar mt-2">
                                    {subgroups.map(subg => {
                                        const isSelected = activeSubgroup === subg;
                                        return (
                                            <button
                                                key={subg}
                                                onClick={() => setActiveSubgroup(subg)}
                                                style={{ backgroundColor: isSelected ? "#ffa800" : "#0a58ca" }}
                                                className="px-6 py-2.5 rounded-lg font-black uppercase tracking-widest text-[11px] sm:text-[12px] text-white transition-all shadow-md hover:scale-[1.02] active:scale-95 duration-200 min-w-[110px] text-center"
                                            >
                                                {subg}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {Object.keys(groupedData).map((date, dayIdx) => (
                        <div key={dayIdx} className="space-y-6">
                            {/* Date Section Banner (Blue or Green artistic banner) */}
                            <div className="flex justify-center items-center w-full my-6">
                                <div
                                    className="w-full flex items-center justify-center select-none min-h-[50px] sm:min-h-[60px] rounded-xl max-w-xl shadow-sm"
                                    style={{
                                        backgroundColor: Object.values(groupedData[date])[0]?.color && Object.values(groupedData[date])[0]?.color !== '#000000' ? Object.values(groupedData[date])[0].color : primaryThemeColor,
                                    }}
                                >
                                    <div className="flex flex-row items-center justify-center gap-2 pt-1 pb-1">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="drop-shadow-sm">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                            <line x1="16" y1="2" x2="16" y2="6"></line>
                                            <line x1="8" y1="2" x2="8" y2="6"></line>
                                            <line x1="3" y1="10" x2="21" y2="10"></line>
                                            <path d="M9 16l2 2 4-4"></path>
                                        </svg>
                                        <h2 className="text-[20px] sm:text-[26px] drop-shadow-sm tracking-wider lowercase capitalize translate-y-[2px]" style={{ fontFamily: 'Drina, cursive', color: '#ffffff' }}>
                                            {date} {isTepezzaActive ? " | Chairman: Alejandro Román" : ""}
                                        </h2>
                                    </div>
                                </div>
                            </div>

                            {/* Tables Layout */}
                            <div className="space-y-4">
                                {Object.values(groupedData[date]).map((block, blockIdx) => (
                                    <React.Fragment key={blockIdx}>
                                        {block.name !== 'Sin Bloque' && (
                                            <div className="px-6 py-1.5 rounded-full font-black uppercase text-[10px] tracking-wider italic w-max mt-4 shadow-sm border border-slate-100 text-white"
                                                style={{ backgroundColor: block.color && block.color !== '#000000' ? block.color : primaryThemeColor }}>
                                                {block.name}
                                            </div>
                                        )}

                                        {/* Header of columns in Drina font as in mockups */}
                                        <div
                                            className="flex w-full gap-4 sm:gap-6 mb-2 mt-4 px-4 pb-2 pt-2 select-none items-center min-h-[40px] sm:min-h-[50px] rounded-xl shadow-sm"
                                            style={{
                                                backgroundColor: block.color && block.color !== '#000000' ? block.color : primaryThemeColor,
                                            }}
                                        >
                                            <div className="w-24 sm:w-36 shrink-0 font-bold text-center text-[20px] sm:text-[24px]" style={{ fontFamily: 'Drina, cursive', color: '#ffffff' }}>
                                                Hora
                                            </div>
                                            <div className="flex-grow font-bold text-center text-[20px] sm:text-[24px]" style={{ fontFamily: 'Drina, cursive', color: '#ffffff' }}>
                                                Intervención
                                            </div>
                                            <div className="w-24 sm:w-44 shrink-0 font-bold text-center text-[20px] sm:text-[24px]" style={{ fontFamily: 'Drina, cursive', color: '#ffffff' }}>
                                                Speaker
                                            </div>
                                        </div>

                                        {/* Rows iteration */}
                                        <div className="space-y-3">
                                            {block.events.map((event, i) => {
                                                const icon = getIcon(event.activity_type);
                                                const isSpecial = event.activity_type !== 'session';

                                                return (
                                                    <div
                                                        key={i}
                                                        className="w-full py-4 border-b border-slate-100 flex flex-row items-center gap-4 sm:gap-6 transition-all duration-300 hover:bg-slate-50/50"
                                                    >
                                                        {/* Hour Capsule Button */}
                                                        <div className="w-24 sm:w-36 shrink-0 flex justify-center">
                                                            <div className="flex items-center gap-1 w-full justify-center">
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1 hidden sm:block">
                                                                    <circle cx="12" cy="12" r="10"></circle>
                                                                    <polyline points="12 6 12 12 16 14"></polyline>
                                                                </svg>
                                                                <div
                                                                    className="px-4 py-1 text-white font-extrabold text-[10px] sm:text-[11px] text-center tracking-tight select-none min-h-[25px] sm:min-h-[30px] flex items-center justify-center shadow-sm w-full rounded-xl"
                                                                    style={{
                                                                        backgroundColor: block.color && block.color !== '#000000' ? block.color : primaryThemeColor,
                                                                    }}
                                                                >
                                                                    {event.start_time.slice(0, 5)} {parseInt(event.start_time.split(':')[0]) >= 12 ? 'p.m.' : 'a.m.'} - {event.end_time.slice(0, 5)} {parseInt(event.end_time.split(':')[0]) >= 12 ? 'p.m.' : 'a.m.'}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Intervention Topic Title */}
                                                        <div className="flex-grow text-center px-2">
                                                            <h3 className="text-[14px] sm:text-[16px] leading-tight font-medium" style={{ fontFamily: 'Poppins, sans-serif', color: '#000000' }}>
                                                                {event.title}
                                                            </h3>
                                                            {event.moderator && (
                                                                <p className="text-[12px] sm:text-[14px] font-medium mt-1 tracking-wider text-slate-500" style={{ fontFamily: 'Poppins, sans-serif' }}>
                                                                    Moderador: {event.moderator}
                                                                </p>
                                                            )}
                                                        </div>

                                                        {/* Speakers list in custom brand color */}
                                                        <div className="w-24 sm:w-44 shrink-0 flex items-center justify-center px-2">
                                                            {isSpecial && icon ? (
                                                                <div className="text-xl sm:text-2xl filter drop-shadow-sm select-none">{icon}</div>
                                                            ) : (
                                                                <div className="flex flex-col gap-0.5 text-center w-full">
                                                                    {event.speakers.map((speaker, idx) => (
                                                                        <div key={idx} className="flex flex-col sm:flex-row items-center justify-center gap-1 w-full">
                                                                            <span
                                                                                style={{ color: block.color && block.color !== '#000000' ? block.color : finalFontColor, fontFamily: 'Drina, cursive' }}
                                                                                className="text-[18px] sm:text-[22px] leading-tight"
                                                                            >
                                                                                {speaker.name}
                                                                            </span>
                                                                            {speaker.country && (
                                                                                <span className="text-[11px] sm:text-sm filter drop-shadow-sm select-none" title={speaker.country}>
                                                                                    {getFlagEmoji(speaker.country)}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {pageDesign.subgroupPosition === 'bottom' && subgroups.length > 0 && (
                <div className="flex justify-center gap-3 w-full overflow-x-auto pb-2 px-4 custom-scrollbar mt-10 mb-4">
                    {subgroups.map(subg => {
                        const isSelected = activeSubgroup === subg;
                        return (
                            <button
                                key={subg}
                                onClick={() => setActiveSubgroup(subg)}
                                style={{ backgroundColor: isSelected ? "#ffa800" : "#0a58ca" }}
                                className="px-6 py-2.5 rounded-lg font-black uppercase tracking-widest text-[11px] sm:text-[12px] text-white transition-all shadow-md hover:scale-[1.02] active:scale-95 duration-200 min-w-[110px] text-center"
                            >
                                {subg}
                            </button>
                        );
                    })}
                </div>
            )}

            {(() => {
                const legalConfig = getActiveLegalConfig(activeGroup, activeSubgroup);
                return legalConfig?.showLegalText && legalConfig?.legalTextUrl && (
                    <footer className="w-full p-4 sm:p-6 mt-auto flex justify-center items-center z-40 select-none bg-transparent">
                        {/\.(pdf)$/i.test(legalConfig.legalTextUrl) ? (
                            <a href={`${legalConfig.legalTextUrl}${legalConfig.legalTextUrl.includes('?') ? '&' : '?'}v=${CACHE_BUSTER}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline font-bold text-sm">Ver Información Legal</a>
                        ) : (
                            <img
                                src={`${legalConfig.legalTextUrl}${legalConfig.legalTextUrl.includes('?') ? '&' : '?'}v=${CACHE_BUSTER}`}
                                alt="Información Legal"
                                className="max-w-[90vw] md:max-w-2xl h-auto object-contain pointer-events-auto"
                            />
                        )}
                    </footer>
                );
            })()}
        </div>
    );
};

export default Agenda;
